import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { useSearchFavorites, type SearchFavorite } from '@/domain/documents/queries';
import useFavorites from '@/pages/company/components/CompanyNavigation/FavoritesMenu/useFavorites';

import { LayoutCompanyContext } from '@/components/mui/Layouts/Company/LayoutCompanyContext';
import FavoritesListItem from '@/pages/company/components/CompanyNavigation/FavoritesMenu/ListItem';
import ListMui from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ListItemIcon } from '@/components/mui/Layouts/components/Drawer';
import { FavoriteListWrapper } from '@/pages/company/components/CompanyNavigation/FavoritesMenu/StyledComponents';

import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';

const FavoritesMenu = () => {
  const { companyId } = useParams();
  const { data: favoritesList } = useSearchFavorites(companyId ?? '');
  const { toggle } = useContext(LayoutCompanyContext);

  const { isCurrentUser, getUserBySearchLink } = useFavorites();

  const entries = useMemo<SearchFavorite[]>(() => {
    const { userFavs, otherFavs } = (favoritesList ?? []).reduce<{
      userFavs: SearchFavorite[];
      otherFavs: SearchFavorite[];
    }>(
      (acc, fav) => {
        const user = getUserBySearchLink(fav.query);
        if (!user) return { ...acc, otherFavs: [...acc.otherFavs, fav] };
        return {
          ...acc,
          userFavs: isCurrentUser(user.userId) ? [fav, ...acc.userFavs] : [...acc.userFavs, fav],
        };
      },
      { userFavs: [], otherFavs: [] },
    );
    return [...userFavs, ...otherFavs];
  }, [favoritesList, isCurrentUser, getUserBySearchLink]);

  if (!toggle) return null;

  return (
    <ListMui component="nav">
      <ListItem component="div">
        <ListItemIcon sx={{ color: 'inherit' }}>
          <StarBorderOutlinedIcon />
        </ListItemIcon>
        <ListItemText disableTypography sx={{ fontWeight: 500 }}>
          <FormattedMessage id="company.favorites" defaultMessage="Favorites" />
        </ListItemText>
      </ListItem>
      <FavoriteListWrapper>
        {entries.map((entry) => (
          <FavoritesListItem key={entry.ID ?? `${entry.type}:${entry.query}:${entry.label}`} entry={entry} />
        ))}
      </FavoriteListWrapper>
    </ListMui>
  );
};

export default FavoritesMenu;
